import React, { useState } from "react";
import { 
    Breadcrumb, 
    NeoButton, 
    NeoInput, 
    NeoTabs, 
    NeoLoader,
    NeoBadge,
    NeoCard,
    NeoDatePicker
} from "../../../components/Common";
import { usePerformance } from "./hooks/usePerformance";

import RankingTable from "./components/RankingTable";
import EmployeeDetailModal from "./components/EmployeeDetailModal";
import { METRIC_GROUP_LABELS } from "../../../constants/performance";

/**
 * Performance Page — Ranking de Colaboradores
 *
 * Padrão Neo Design System (Taça Outono):
 * Breadcrumb → NeoCard(título, subtítulo, headerIcon, headerActions) → NeoTabs → Conteúdo
 */
const PerformancePage = () => {
    document.title = "Performance | NeoApp - Sistema de Gestão";

    const {
        snapshots,
        excludedSnapshots,
        loading,
        recalculating,
        isCurrentPeriod,
        selectedPeriod,
        selectedGroup,
        startDate,
        endDate,
        periodOptions,
        setSelectedPeriod,
        setSelectedGroup,
        setStartDate,
        setEndDate,
        handleRecalculate,
    } = usePerformance();

    const [detailModal, setDetailModal] = useState({ open: false, employee: null });

    const handleViewDetail = (employee) => {
        setDetailModal({ open: true, employee });
    };

    // Tabs de grupo para NeoTabs
    const groupTabs = [
        { id: "all", label: "Todos", icon: "mdi-account-group" },
        { id: "professor", label: METRIC_GROUP_LABELS.professor, icon: "mdi-school" },
        { id: "consultor", label: METRIC_GROUP_LABELS.consultor, icon: "mdi-handshake" },
        { id: "gerente", label: METRIC_GROUP_LABELS.gerente, icon: "mdi-shield-crown" },
    ];

    return (
        <React.Fragment>
            {/* Breadcrumb */}
            <div className="mb-3">
                <Breadcrumb title="Performance" />
            </div>

            {/* Card Principal */}
            <NeoCard
                title="Ranking de Colaboradores"
                subtitle="Classificação por métricas de desempenho mensal."
                headerIcon="mdi-podium-gold"
                headerActions={
                    isCurrentPeriod ? (
                        <NeoButton
                            variant="primary"
                            size="sm"
                            icon="mdi-refresh"
                            onClick={handleRecalculate}
                            loading={recalculating}
                            loadingText="Recalculando..."
                        >
                            Recalcular
                        </NeoButton>
                    ) : (
                        <NeoBadge color="secondary" icon="mdi-lock-outline">
                            Histórico
                        </NeoBadge>
                    )
                }
            >
                {/* Barra de Filtros — Período + Grupo lado a lado */}
                <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-3">
                    {/* Tabs de Grupo */}
                    <NeoTabs
                        tabs={groupTabs}
                        activeTab={selectedGroup}
                        onChangeTab={setSelectedGroup}
                    />

                    {/* Filtro de Período */}
                    <div className="d-flex align-items-center gap-2">
                        <NeoInput
                            type="select"
                            value={selectedPeriod}
                            onChange={(e) => setSelectedPeriod(e.target.value)}
                            options={periodOptions}
                            size="sm"
                            className="mb-0"
                        />

                        {selectedPeriod === "custom" && (
                            <>
                                <NeoDatePicker
                                    value={startDate}
                                    onChange={setStartDate}
                                    placeholder="Início"
                                    size="sm"
                                    className="mb-0"
                                />
                                <NeoDatePicker
                                    value={endDate}
                                    onChange={setEndDate}
                                    placeholder="Fim"
                                    size="sm"
                                    className="mb-0"
                                />
                            </>
                        )}
                    </div>
                </div>

                {/* Conteúdo: Loading ou Dados */}
                {loading ? (
                    <div className="text-center py-5">
                        <NeoLoader type="inline" size="lg" variant="accent" label="Carregando dados de performance..." />
                    </div>
                ) : (
                    <RankingTable 
                        snapshots={snapshots}
                        excludedSnapshots={excludedSnapshots}
                        onViewDetail={handleViewDetail} 
                    />
                )}
            </NeoCard>

            {/* Detail Modal */}
            <EmployeeDetailModal
                isOpen={detailModal.open}
                onClose={() => setDetailModal({ open: false, employee: null })}
                employee={detailModal.employee}
            />
        </React.Fragment>
    );
};

export default PerformancePage;
